import * as B from '@blueprintjs/core';
import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';

import {CopyableText} from 'app/components/CopyableText';
import Modal from 'app/components/Modal/Modal';
import {usePushNotification} from 'app/components/Notification';
import {ApiShareLinkConfig} from 'app/components/PublicMap/PublicShareView';
import cs from 'app/components/PublicMap/styles.module.scss';
import {NoteSlide, makeStorySlides} from 'app/components/StoryView/storySlides';
import {api} from 'app/modules/Remote';
import {ApiFeature, ApiNote, ApiReport, ReportState} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {DEFAULT_COLORS} from 'app/pages/MonitorProjectView/SwatchColorPicker';
import {StateApiNote, hydrateApiNote, noteDisplayName} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import {ApiResponseError} from 'app/utils/apiUtils';
import {REGRID_FEATURE_COLLECTION_ID} from 'app/utils/constants';
import * as layerUtils from 'app/utils/layerUtils';
import * as routeUtils from 'app/utils/routeUtils';

const EMBED_TEMPLATE = `<iframe width="100%" height="100%" src="<EMBED>?embedded=true" frameborder="0" allowfullscreen sandbox="allow-same-origin allow-scripts allow-top-navigation-by-user-activation"></iframe>`;

// The story has no org roster; bylines resolve through the name the API recorded.
const NO_ORG_NAMES = new Map<string, string>();

interface Props {
  isOpen: boolean;
  onClose: () => void;
  report: ApiReport<string>;
  projectId: string;
  /** The report's own property. */
  feature: I.ImmutableOf<ApiFeature>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  /** Every part of the property, ordered to match the report's `currentPropertyPartIndex`. */
  propertyParts: I.ImmutableOf<ApiFeature[]>;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  overlaySettings: GeometryOverlaySetting[];
  organization: I.ImmutableOf<ApiOrganization>;
}

/**
 * Makes a public link to a report's story. Builds the slides the way the public page will,
 * so the author sees the same note count, and the same notes left out for unshareable
 * imagery, that a viewer will. Only overlays those slides turn on go into the link.
 */
const ShareStoryDialog: React.FunctionComponent<Props> = ({
  isOpen,
  onClose,
  report,
  projectId,
  feature,
  featureCollection,
  propertyParts,
  overlayFeatureCollections,
  overlaySettings,
  organization,
}) => {
  const [shareId, setShareId] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const pushNotification = usePushNotification();

  const featureId = feature.get('id');
  const featureCollectionId = featureCollection.get('id');

  const reportQuery = useQuery({
    queryKey: ['story-report', featureId, report.id],
    queryFn: async () => {
      const response = await api.reports(featureId).get(report.id);
      return response.get('data').toJS() as ApiReport<ReportState>;
    },
    enabled: isOpen,
  });

  const noteFeatureIds = React.useMemo(
    () =>
      report.isMultilocation
        ? propertyParts.map((f) => f!.get('id') as number).toArray()
        : [featureId],
    [report.isMultilocation, propertyParts, featureId]
  );
  const notesQuery = useQuery({
    queryKey: ['story-notes', projectId, featureCollectionId, noteFeatureIds],
    queryFn: async () => {
      const notesApi = api.featureCollections.features(featureCollectionId);
      const responses = await Promise.all(
        noteFeatureIds.map((id) =>
          notesApi.listNotes(projectId, id, {perPage: 100}, {getAllPages: 2})
        )
      );
      return responses
        .flatMap((response) => response.get('data').toJS() as ApiNote[])
        .map((note) =>
          hydrateApiNote(note, {name: noteDisplayName(note, NO_ORG_NAMES), isAuthor: false})
        );
    },
    enabled: isOpen,
  });

  const story = React.useMemo(() => {
    if (!reportQuery.data || !notesQuery.data) {
      return null;
    }
    return makeStorySlides({
      report: reportQuery.data,
      notes: notesQuery.data as StateApiNote[],
      anchorFeature: feature,
      propertyParts,
      organizationName: organization.get('name'),
      enrolledLayerKeys: layerUtils.getEnrolledLayerKeysForFeatureCollection(featureCollection),
      omitUnshareable: true,
    });
  }, [reportQuery.data, notesQuery.data, feature, propertyParts, organization, featureCollection]);

  const noteSlides = React.useMemo(
    () => story?.slides.filter((s): s is NoteSlide => s.kind === 'note') ?? [],
    [story]
  );

  // The overlays any slide shows: the note's own, plus those the report page toggled on.
  // Regrid parcels are licensed and never leave the app.
  const sharedOverlays = React.useMemo<ApiFeatureCollection[]>(() => {
    const overlays = overlayFeatureCollections.toJS() as ApiFeatureCollection[];
    const idByName = new Map(overlays.map((fc) => [fc.name, fc.id]));
    const ids = new Set<number>();
    for (const slide of noteSlides) {
      slide.note.overlayFeatureCollectionIds?.forEach((id) => ids.add(id));
      for (const [name, visible] of Object.entries(slide.overlayVisibilityByName ?? {})) {
        const id = idByName.get(name);
        if (visible && id !== undefined) {
          ids.add(id);
        }
      }
    }
    return overlays.filter((fc) => ids.has(fc.id) && fc.id !== REGRID_FEATURE_COLLECTION_ID);
  }, [overlayFeatureCollections, noteSlides]);

  const link = shareId ? routeUtils.makeAppUrl(`/p/s/${shareId}`) : '';
  const embedCode = EMBED_TEMPLATE.replace('<EMBED>', link);

  const createShareLink = async () => {
    setIsCreating(true);
    const config: ApiShareLinkConfig = {
      lens_id: feature.getIn(['properties', 'lensId']),
      feature_collection_id: featureCollectionId,
      report_id: report.id,
      report_presentation: 'story',
      scenes: [],
      overlay_feature_collection_ids: sharedOverlays.map((fc) => fc.id),
      overlay_configs: sharedOverlays.map((fc) => {
        const setting = overlaySettings.find((s) => s.name === fc.name);
        return {
          feature_collection_id: fc.id,
          style: {
            color: setting?.color ?? DEFAULT_COLORS[0],
            default_filled: setting?.defaultFilled ?? false,
            default_unfilled: setting?.defaultUnfilled ?? false,
            property_color_rule: setting?.propertyColorRule ?? null,
          },
        };
      }),
    };
    try {
      const response = await api.makeShareLink(config);
      setShareId(response.getIn(['data', 'id']));
      recordEvent('Created story share link', {
        reportId: report.id,
        noteCount: noteSlides.length,
        unshareableCount: story?.unshareableCount ?? 0,
      });
    } catch (e) {
      const {error} = e as ApiResponseError;
      pushNotification({
        message: `Something went wrong: ${error.message}`,
        autoHideDuration: 3000,
        options: {intent: B.Intent.DANGER},
      });
    } finally {
      setIsCreating(false);
    }
  };

  const isLoading = reportQuery.isPending || notesQuery.isPending;
  const loadFailed = reportQuery.isError || notesQuery.isError;
  const unshareableCount = story?.unshareableCount ?? 0;

  return (
    <Modal
      isOpen={isOpen}
      headerText="Share interactive report"
      canOutsideClickClose={false}
      onClose={() => {
        setShareId(null);
        onClose();
      }}
      hideActions={true}
    >
      <div className={cs.shareModal}>
        <div>
          <div style={{marginTop: '2rem'}}>
            Share this report as an interactive map story with partners and colleagues—no Lens
            account needed.{' '}
            <a
              href={'https://lens.tech/support/article/151-lens-share-link#share'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more here.
            </a>
          </div>
          <table className={cs.shareModalDetails}>
            <tbody>
              <tr>
                <th colSpan={2}>
                  <div className={cs.sectionHeader}>
                    <div>Details</div>
                    <div className={cs.sectionHeaderDivider} />
                  </div>
                </th>
              </tr>
              <tr>
                <th>Property</th>
                <td>{feature.getIn(['properties', 'name'])}</td>
              </tr>
              <tr>
                <th>Report</th>
                <td>{report.title}</td>
              </tr>
              <tr>
                <th>Notes</th>
                <td>
                  {loadFailed ? (
                    <B.Callout intent={B.Intent.DANGER}>
                      We couldn’t load this report’s notes. Close this dialog and try again.
                    </B.Callout>
                  ) : isLoading ? (
                    <B.Spinner size={B.SpinnerSize.SMALL} />
                  ) : (
                    <>
                      <div>
                        {noteSlides.length} {noteSlides.length === 1 ? 'note' : 'notes'}
                      </div>
                      {unshareableCount > 0 && (
                        <B.Callout intent={B.Intent.WARNING} style={{marginTop: '0.5rem'}}>
                          Data from {unshareableCount} {unshareableCount === 1 ? 'note' : 'notes'}{' '}
                          can’t be shared publicly because of licensing restrictions, so the story
                          will leave {unshareableCount === 1 ? 'it' : 'them'} out.
                        </B.Callout>
                      )}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>{sharedOverlays.length === 1 ? 'Overlay' : 'Overlays'}</th>
                <td>
                  {sharedOverlays.length === 0 ? (
                    <div>No overlays shown</div>
                  ) : (
                    <ul>
                      {sharedOverlays.map((fc) => (
                        <li key={fc.id}>{fc.name}</li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <B.Button
                    className={cs.shareLinkConfirmButton}
                    disabled={isCreating || !story}
                    onClick={() => (shareId ? setShareId(null) : createShareLink())}
                    loading={isCreating}
                    text={shareId ? 'Edit (makes new link)' : 'Next'}
                    intent={B.Intent.PRIMARY}
                  />
                </td>
              </tr>
              {shareId && (
                <>
                  <tr>
                    <th colSpan={2}>
                      <div className={cs.sectionHeader}>
                        <div>Share</div>
                        <div className={cs.sectionHeaderDivider} />
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th colSpan={2}>
                      <CopyableText
                        description={
                          <span>
                            <div className={cs.shareSectionHead}>
                              <b>Lens Story Link</b>
                            </div>
                            <span className={cs.shareLinkShareDetails}>
                              Send an interactive story to external partners
                            </span>
                          </span>
                        }
                        copyableText={link}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th colSpan={2}>
                      <CopyableText
                        description={
                          <span>
                            <div className={cs.shareSectionHead}>
                              <b>Lens Story Embed</b>
                            </div>
                            <span className={cs.shareLinkShareDetails}>
                              Include an interactive story on a webpage
                            </span>
                          </span>
                        }
                        copyableText={embedCode}
                      />
                    </th>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default ShareStoryDialog;
